import React from 'react';
import type { AgingColumn, AgingCurrencyBlock, AgingFigures, AgingReport } from '../../lib/accountsPayableReportsApi';
import { agingCellHref } from './drillDown';
import { billCountLabel, formatReportDay, formatReportMoney, supplierLabel } from './format';
import ReportLink from './ReportLink';

/**
 * The aging report as tables, one per currency (a currency is never added to another). The
 * columns, their labels and the due-day range each one covers come from the server's answer.
 * Every non-zero figure links to the bills it adds up; a zero is a dash, not a link to an empty
 * list. Used by the Reports page (every supplier: a list, no line between columns) and by the supplier
 * page (one supplier: a detail page, a line between every column).
 */

const TH = 'whitespace-nowrap px-3 py-2 text-right text-xs font-semibold text-slate-600';

function Cell({
  amount,
  currency,
  column,
  supplierName,
  who,
}: {
  amount: number;
  currency: string;
  column: AgingColumn;
  supplierName: string | null;
  who: string;
}) {
  if (!amount) {
    return (
      <td className="px-3 py-2 text-right text-slate-400">
        <span aria-hidden="true">--</span>
        <span className="sr-only">nothing</span>
      </td>
    );
  }
  const money = formatReportMoney(amount, currency);
  return (
    <td className={`px-3 py-2 text-right tabular-nums ${column.key === 'total' ? 'font-semibold text-slate-900' : ''}`}>
      <ReportLink
        href={agingCellHref({ currency, supplierName }, column)}
        label={`${money}, ${who}, ${column.label}. Open these bills`}
      >
        {money}
      </ReportLink>
    </td>
  );
}

function FigureRow({
  figures,
  buckets,
  currency,
  supplierName,
  name,
  who,
  isTotal,
}: {
  figures: AgingFigures;
  buckets: AgingColumn[];
  currency: string;
  supplierName: string | null;
  name: string;
  who: string;
  isTotal?: boolean;
}) {
  return (
    <tr className={isTotal ? 'border-t-2 border-slate-300 bg-slate-50 font-semibold' : 'border-b border-slate-100'}>
      <th scope="row" className="px-3 py-2 text-left text-sm font-medium text-slate-800">
        {name}
      </th>
      {buckets.map((column) => (
        <Cell
          key={column.key}
          amount={figures[column.key]}
          currency={currency}
          column={column}
          supplierName={supplierName}
          who={who}
        />
      ))}
      <td className="px-3 py-2 text-right text-xs text-slate-600">{billCountLabel(figures.billCount)}</td>
    </tr>
  );
}

export const AgingTable: React.FC<{
  report: AgingReport;
  block: AgingCurrencyBlock;
  /**
   * Show only the currency's totals row, named for this supplier (the supplier page, whose report
   * is already filtered to one supplier). Without it, one row per supplier and a total row.
   */
  onlySupplier?: string;
}> = ({ report, block, onlySupplier }) => {
  const { buckets } = report;
  const heading = `Aging in ${block.currency}`;
  return (
    <section aria-label={heading} className="mb-6">
      <h3 className="mb-2 text-sm font-semibold text-slate-900">{block.currency}</h3>
      <div
        role="region"
        aria-label={`${heading}, scrolls sideways on a narrow screen`}
        tabIndex={0}
        className="relative overflow-x-auto rounded-lg border border-slate-200 bg-white"
      >
        <table className={`data-table ${onlySupplier ? 'data-table-grid ' : ''}min-w-[860px] w-full text-sm`}>
          <caption className="sr-only">
            {onlySupplier
              ? `Amounts owed to ${onlySupplier} in ${block.currency}, by how many days past their due date on ${formatReportDay(report.asOf)}.`
              : `Amounts owed in ${block.currency}, by supplier and by how many days past their due date on ${formatReportDay(report.asOf)}.`}
          </caption>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-slate-600">
                {onlySupplier ? 'Owed' : 'Supplier'}
              </th>
              {buckets.map((column) => (
                <th key={column.key} scope="col" className={TH}>
                  {column.label}
                </th>
              ))}
              <th scope="col" className={TH}>
                Bills
              </th>
            </tr>
          </thead>
          <tbody>
            {onlySupplier ? (
              <FigureRow
                figures={block.totals}
                buckets={buckets}
                currency={block.currency}
                supplierName={onlySupplier}
                name={onlySupplier}
                who={onlySupplier}
              />
            ) : (
              <>
                {block.suppliers.map((row) => (
                  <FigureRow
                    key={row.supplierName}
                    figures={row}
                    buckets={buckets}
                    currency={block.currency}
                    supplierName={row.supplierName}
                    name={supplierLabel(row.supplierName)}
                    who={supplierLabel(row.supplierName)}
                  />
                ))}
                <FigureRow
                  figures={block.totals}
                  buckets={buckets}
                  currency={block.currency}
                  supplierName={null}
                  name={`All suppliers (${block.totals.supplierCount})`}
                  who="all suppliers"
                  isTotal
                />
              </>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default AgingTable;
